import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  Output,
} from "@angular/core";
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from "@angular/forms";
import { DatePickerModule } from "primeng/datepicker";
import { fromTargetMonth, toTargetMonth } from "./month-picker.utils";

/** PrimeNG dateFormat: "MM yy" => localized month name + year; "mm/yy" => 07/2026. */
export type MonthPickerDisplayFormat = "long" | "numeric";

/**
 * A month + year picker whose form value is a Date on the last day of the picked month at UTC midnight
 * (or null), the shape stored in Transaction.targetMonth. Wraps PrimeNG's month-view datepicker.
 *
 * Usage:
 *   <app-month-picker formControlName="targetMonth" [placeholder]="placeholder" />
 *   <app-month-picker [(ngModel)]="selectedMonth" [inline]="true" [heading]="heading" />   (inside a popover)
 */
@Component({
  selector: "app-month-picker",
  templateUrl: "./month-picker.component.html",
  standalone: true,
  imports: [FormsModule, DatePickerModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MonthPickerComponent),
      multi: true,
    },
  ],
})
export class MonthPickerComponent implements ControlValueAccessor {
  @Input() placeholder = "";
  @Input() inputId = "";
  @Input() displayFormat: MonthPickerDisplayFormat = "long";
  /** Render the calendar panel in place instead of as an overlay. Use inside popovers. */
  @Input() inline = false;
  /** Title shown at the top of the calendar panel, so an inline host needs no wrapper of its own. */
  @Input() heading = "";
  /** Paint the input as invalid. The owning form decides when; the picker itself cannot produce a bad value. */
  @Input() invalid = false;
  /** Fires after each pick or clear with the new value. Lets an inline host (a popover) close itself. */
  @Output() monthSelected = new EventEmitter<Date | null>();

  /** What PrimeNG shows: a local Date somewhere in the picked month. */
  public selectedDate: Date | null = null;
  public isDisabled = false;
  /** Bounds match the API's four-digit-year range, so the picker cannot produce a value the API rejects. */
  public readonly minDate = new Date(1000, 0, 1);
  public readonly maxDate = new Date(9999, 11, 1);

  // Registered by the forms API once the control is bound.
  private onChange?: (value: Date | null) => void;
  private onTouched?: () => void;

  constructor(private changeDetectorRef: ChangeDetectorRef) {}

  /** Inline, the host (a popover) is already the box, so the panel drops its own border and padding. */
  public get panelStyle(): Record<string, string> {
    const size = { width: "15rem", "min-width": "275px" };
    return this.inline ? { ...size, border: "none", padding: "0" } : size;
  }

  public get dateFormat(): string {
    return this.displayFormat === "numeric" ? "mm/yy" : "MM yy";
  }

  public onDateChange(date: Date | null): void {
    this.selectedDate = date;
    const targetMonth = date ? toTargetMonth(date) : null;
    this.onChange?.(targetMonth);
    this.monthSelected.emit(targetMonth);
  }

  public onBlur(): void {
    this.onTouched?.();
  }

  public writeValue(value: Date | null | undefined): void {
    this.selectedDate = value ? fromTargetMonth(value) : null;
    this.changeDetectorRef.markForCheck();
  }

  public registerOnChange(fn: (value: Date | null) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
    this.changeDetectorRef.markForCheck();
  }
}
